import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: ''
      });
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="hero-section" style={{ minHeight: '50vh' }} data-testid="contact-hero-section">
        <div className="hero-content fade-in-up">
          <h1 className="hero-title" data-testid="contact-title">Contact Us</h1>
          <div className="decorative-line"></div>
          <p className="hero-subtitle" data-testid="contact-subtitle">
            Get in touch to discuss your upcoming event
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section" style={{ background: 'white' }} data-testid="contact-content-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div data-testid="contact-info">
              <h2 className="text-3xl font-bold text-[#8b4513] mb-6">Get in Touch</h2>
              <p className="text-[#6b4423] mb-8">
                We'd love to hear about your event! Whether you need catering services, full event
                planning, or just have questions, our team is here to help.
              </p>

              <div className="space-y-6">
                <div className="flex items-start" data-testid="contact-phone">
                  <div className="w-12 h-12 bg-[#fff5e6] rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <Phone size={24} className="text-[#d2691e]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#8b4513] mb-1">Phone</h3>
                    <p className="text-[#6b4423]">(240) 605-4915</p>
                    <p className="text-sm text-[#6b4423]">Monday - Saturday, 9am - 6pm</p>
                  </div>
                </div>

                <div className="flex items-start" data-testid="contact-email">
                  <div className="w-12 h-12 bg-[#fff5e6] rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <Mail size={24} className="text-[#d2691e]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#8b4513] mb-1">Email</h3>
                    <p className="text-[#6b4423]">info@jfraifamilycatering.com</p>
                    <p className="text-sm text-[#6b4423]">We'll respond within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start" data-testid="contact-location">
                  <div className="w-12 h-12 bg-[#fff5e6] rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <MapPin size={24} className="text-[#d2691e]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#8b4513] mb-1">Location</h3>
                    <p className="text-[#6b4423]">Serving the Greater Metro Area</p>
                    <p className="text-sm text-[#6b4423]">Travel available for larger events</p>
                  </div>
                </div>

                <div className="flex items-start" data-testid="contact-hours">
                  <div className="w-12 h-12 bg-[#fff5e6] rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <Clock size={24} className="text-[#d2691e]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#8b4513] mb-1">Business Hours</h3>
                    <p className="text-[#6b4423]">Mon - Fri: 9:00 AM - 6:00 PM</p>
                    <p className="text-[#6b4423]">Saturday: 10:00 AM - 4:00 PM</p>
                    <p className="text-[#6b4423]">Sunday: Closed</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="card" data-testid="contact-form-card">
              <h2 className="text-3xl font-bold text-[#8b4513] mb-6">Send a Message</h2>
              <form onSubmit={handleSubmit} data-testid="contact-form">
                <div className="form-group">
                  <label className="form-label">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="form-input"
                    required
                    data-testid="contact-form-name"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="form-input"
                    required
                    data-testid="contact-form-email"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="form-input"
                    required
                    data-testid="contact-form-phone"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Message *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="form-textarea"
                    rows="5"
                    placeholder="Tell us about your event or inquiry..."
                    required
                    data-testid="contact-form-message"
                  ></textarea>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full bg-[#d2691e] hover:bg-[#c25a1a]"
                  data-testid="contact-form-submit"
                >
                  {loading ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links Section */}
      <section className="section" style={{ background: '#fff5e6' }} data-testid="contact-quick-links">
        <div className="text-center">
          <h2 className="section-title">Need Something Specific?</h2>
          <div className="decorative-line"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8 max-w-3xl mx-auto">
            <div className="card">
              <h3 className="text-xl font-bold mb-3 text-[#8b4513]">Catering Quote</h3>
              <p className="text-[#6b4423] mb-4">
                Looking for catering services? Get a detailed quote for your event.
              </p>
              <a href="/catering" className="text-[#d2691e] font-semibold hover:underline">
                View Catering Menu →
              </a>
            </div>
            <div className="card">
              <h3 className="text-xl font-bold mb-3 text-[#8b4513]">Event Planning Quote</h3>
              <p className="text-[#6b4423] mb-4">
                Need full event planning? Request a custom proposal for your occasion.
              </p>
              <a href="/events" className="text-[#d2691e] font-semibold hover:underline">
                View Event Packages →
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;